import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Sparkles, Zap, Loader2 } from "lucide-react";
import api from "../api";
import { usePageTitle } from "../components/common";

export default function PaymentSuccess() {
  usePageTitle("Paiement confirmé");
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const type = params.get("type"); // guide | abo | (undefined => séance)
  const [status, setStatus] = useState(null);
  const [tries, setTries] = useState(0);

  useEffect(() => {
    if (!sessionId || type === "abo") return;
    let stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (stop) return;
        setStatus(data);
        if (data.payment_status !== "paid" && tries < 12) {
          setTimeout(() => setTries((t) => t + 1), 2000);
        }
      } catch {
        if (tries < 12) setTimeout(() => setTries((t) => t + 1), 2000);
      }
    };
    poll();
    return () => { stop = true; };
  }, [sessionId, tries, type]);

  if (!sessionId)
    return <div className="container-x py-24 text-center text-[#7A5D58]">Session introuvable.</div>;

  // Subscription (Entre Lui et Moi): no order to poll — confirmation + espace link.
  if (type === "abo")
    return (
      <div className="container-x py-20 sm:py-28 fade-up">
        <div className="max-w-xl mx-auto text-center card-soft p-10">
          <div className="w-16 h-16 rounded-full bg-[#C9A56A]/15 flex items-center justify-center mx-auto">
            <Sparkles className="text-[#C9A56A]" size={30} />
          </div>
          <h1 className="mt-6 font-serif text-3xl sm:text-4xl font-medium text-[#5A3A4B]">Ton abonnement est confirmé ✨</h1>
          <p className="mt-4 text-[#7A5D58] leading-relaxed">
            Merci pour ta confiance. Ton courrier <strong>Entre Lui et Moi</strong> sera préparé avec soin chaque mois.
            Tu peux gérer ton abonnement à tout moment depuis ton espace.
          </p>
          <button onClick={() => navigate("/espace")} className="btn-primary mt-8" data-testid="success-espace-btn">
            Accéder à mon espace
          </button>
        </div>
      </div>
    );

  if (!status || status.payment_status !== "paid")
    return (
      <div className="container-x py-28 text-center fade-up">
        <Loader2 className="animate-spin mx-auto text-[#C9A56A]" size={40} />
        <p className="mt-6 text-[#7A5D58]">Nous confirmons ton paiement en toute sécurité…</p>
      </div>
    );

  // Digital product (Guide Tarot)
  if (type === "guide")
    return (
      <div className="container-x py-20 sm:py-28 fade-up">
        <div className="max-w-xl mx-auto text-center card-soft p-10">
          <div className="w-16 h-16 rounded-full bg-[#C9A56A]/15 flex items-center justify-center mx-auto">
            <Sparkles className="text-[#C9A56A]" size={30} />
          </div>
          <h1 className="mt-6 font-serif text-3xl sm:text-4xl font-medium text-[#5A3A4B]">Merci pour ta commande ✨</h1>
          <p className="mt-4 text-[#7A5D58] leading-relaxed">
            Ton <strong>Guide Visuel Complet du Tarot</strong> est maintenant disponible dans ton espace,
            rubrique « Mes contenus ».
          </p>
          <button onClick={() => navigate("/espace")} className="btn-primary mt-8" data-testid="success-espace-btn">
            Accéder à mon guide
          </button>
        </div>
      </div>
    );

  const isSos = status.service_slug === "sos-sorciere";

  return (
    <div className="container-x py-20 sm:py-28 fade-up">
      <div className="max-w-xl mx-auto text-center card-soft p-10">
        <div className="w-16 h-16 rounded-full bg-[#C9A56A]/15 flex items-center justify-center mx-auto">
          {isSos ? <Zap className="text-[#C9A56A]" size={30} /> : <Sparkles className="text-[#C9A56A]" size={30} />}
        </div>
        <h1 className="mt-6 font-serif text-3xl sm:text-4xl font-medium text-[#5A3A4B]">
          {isSos ? "SOS Sorcière est en route ⚡" : "Ton espace est réservé ✨"}
        </h1>
        <p className="mt-4 text-[#7A5D58] leading-relaxed whitespace-pre-line">
          {isSos
            ? "Ton paiement a bien été reçu.\n\nComplète maintenant ta question et tes informations.\n\nUne fois ton dossier complet, ta réponse sera préparée sous 24 heures maximum."
            : "Ton paiement a bien été reçu.\n\nIl te reste maintenant à compléter tes informations et à choisir ton créneau."}
        </p>
        <button
          onClick={() => navigate(`/informations?session_id=${sessionId}`)}
          className="btn-primary mt-8"
          data-testid="success-continue-btn"
        >
          Compléter mes informations
        </button>
      </div>
    </div>
  );
}
